import { apiUrl } from "../../utils/api";
import { mailLinkService } from "../../services/mailLinkService";
import { telephonyService } from "../../services/telephonyService";
import { documentService } from "../../services/documentService";
import { displayNameOf } from "../telephony/telephonyUtils";
import { risikoLabel, vertragLabel } from "../vorgang/detail/linkSlots";
import type { RisikoRef, VertragRef } from "../vorgang/detail/linkSlots";
import { bezugKandidaten, type BezugKandidat } from "../notizbloecke/bezugKandidaten";

/**
 * Das Angebot hinter dem `#` im Kundentagebuch (PO 08.09.2026): die Mails,
 * Anrufe, Dokumente, Verträge und Risiken DIESES Kunden.
 *
 * Im Vorgang steht dieselbe Auswahl kostenlos zur Verfügung — dort ist alles
 * schon für die Zeitachse geladen. Das Tagebuch lädt seine Blöcke dagegen über
 * einen eigenen Endpunkt und kennt die Kommunikation nicht. Deshalb wird sie
 * hier nachgeladen, und zwar ERST, wenn jemand zu schreiben beginnt: fünf
 * Abrufe bei jedem Öffnen der Akte, nur damit ein Menü gefüllt wäre, das die
 * meisten nie öffnen, wären nicht zu rechtfertigen.
 *
 * JEDE Quelle fängt ihren Fehler selbst ab — dieselbe Regel wie im Verlauf
 * (ContactArchiv): ein fehlender Postfach-Zugriff darf die Auswahl schmälern,
 * aber nicht das Schreiben stören.
 */

const ruhig = async <T>(was: string, lade: () => Promise<T>, leer: T): Promise<T> => {
    try {
        return await lade();
    } catch (err) {
        console.error(`[Kundentagebuch] ${was} für die #-Auswahl laden fehlgeschlagen`, err);
        return leer;
    }
};

/** Rohe Liste einer der beiden Bezugs-Routen; Fehler ergeben die leere Liste. */
async function ladeListe<T>(url: string): Promise<T[]> {
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error(`API error ${res.status}`);
    const raw: unknown = await res.json();
    return Array.isArray(raw) ? (raw as T[]) : [];
}

/**
 * Die fünf Quellen eines Kontakts, in EINEM Rutsch. Grenze der Dokumenten-
 * Quelle wie im Verlauf: `GET /documents?contactId=` deckt Kontakt-, Vertrags-,
 * Schaden- und Risiko-Dokumente ab, nicht die, die allein an einem Vorgang oder
 * To-do hängen.
 */
export async function ladeBezugQuellen(contactId: number): Promise<BezugKandidat[]> {
    const [mails, anrufe, dokumente, vertraege, risiken] = await Promise.all([
        ruhig("Mails", () => mailLinkService.listByContact(contactId), []),
        ruhig(
            "Anrufe",
            async () => (await telephonyService.listCalls({ contactId, limit: 200 })).calls ?? [],
            [],
        ),
        ruhig("Dokumente", () => documentService.list({ contactId, limit: 200 }), []),
        ruhig(
            "Verträge",
            () => ladeListe<VertragRef>(apiUrl(`contracts/by-contact/${contactId}`)),
            [],
        ),
        ruhig(
            "Risiken",
            () => ladeListe<RisikoRef>(apiUrl(`risk-objects/by-contact/${contactId}`)),
            [],
        ),
    ]);

    return bezugKandidaten({
        mails: mails.map((m) => ({ id: m.id, subject: m.subject, from: m.senderName })),
        // Derselbe Anzeigename wie in der Telefonliste und im Vorgangs-Verlauf.
        anrufe: anrufe.map((c) => ({
            id: c.id,
            displayName: displayNameOf(c),
            startedAt: c.startedAt,
        })),
        dokumente: dokumente.map((d) => ({ id: d.id, fileName: d.fileName })),
        vertraege: vertraege.map((v) => ({ id: v.id, label: vertragLabel(v) })),
        risiken: risiken.map((r) => ({ id: r.id, label: risikoLabel(r) })),
    });
}
